import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Empty } from "./OverviewTab";

const EMPTY_OBJECT = Object.freeze({});
const EMPTY_TRIGGERS = Object.freeze([]);

export default function RuleEngineTab({ version }) {
  const t = useT();
  const [selectedRule, setSelectedRule] = useState("");
  const [referenceFallback, setReferenceFallback] = useState({});
  const classification = version?.classification || EMPTY_OBJECT;
  const triggers = classification.rule_triggers || EMPTY_TRIGGERS;
  const savedReference = classification.annex_viii_rules_reference || EMPTY_OBJECT;
  const matchedTriggers = triggers.filter((trigger) => trigger?.matched !== false);
  const decisiveTriggers = matchedTriggers.filter(
    (trigger) => String(trigger.result || "") === String(classification.suggested_class || "")
  );
  const summaryPool = decisiveTriggers.length ? decisiveTriggers : matchedTriggers;
  const summaryTriggers = [];
  const summaryKeys = new Set();
  summaryPool.forEach((trigger) => {
    const key = `${trigger.rule ?? ""}:${trigger.title ?? ""}`;
    if (!summaryKeys.has(key)) {
      summaryKeys.add(key);
      summaryTriggers.push(trigger);
    }
  });
  const visibleSummaryTriggers = summaryTriggers.slice(0, 2);
  const hasOmittedCriteria = summaryTriggers.length > visibleSummaryTriggers.length;
  const ruleReference = Object.keys(savedReference).length ? savedReference : referenceFallback;
  const rules = useMemo(
    () => Object.entries(ruleReference).sort(([a], [b]) => Number(a) - Number(b)),
    [ruleReference]
  );
  const triggerMap = useMemo(() => {
    const map = new Map();
    triggers.forEach((trigger) => {
      const key = String(trigger.rule);
      map.set(key, [...(map.get(key) || []), trigger]);
    });
    return map;
  }, [triggers]);
  const firstRule = String(triggers[0]?.rule ?? rules[0]?.[0] ?? "");
  const activeRule = selectedRule || firstRule;

  useEffect(() => {
    setSelectedRule("");
  }, [version?.version]);

  useEffect(() => {
    if (Object.keys(savedReference).length) {
      setReferenceFallback(savedReference);
      return undefined;
    }
    let current = true;
    api.get("/reference/mdr-rules")
      .then(({ data }) => {
        if (current) setReferenceFallback(data?.rules || {});
      })
      .catch(() => {
        if (current) setReferenceFallback({});
      });
    return () => { current = false; };
  }, [version?.version, savedReference]);

  if (!version) return <Empty msg={t("ov.no_analysis")} />;
  const selectedTitle = rules.find(([number]) => number === activeRule)?.[1]
    || triggerMap.get(activeRule)?.[0]?.title
    || "";
  const selectedResults = triggerMap.get(activeRule) || [];

  return (
    <section className="grid-card p-4 sm:p-5 space-y-4" data-testid="rule-engine-table">
      <div className="rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] p-4" data-testid="classification-rationale-summary">
        <h2 className="font-semibold">{t("classification.rationale_summary")}</h2>
        <p className="mt-2 text-sm leading-relaxed text-[var(--text-2)]">
          {t("classification.rationale_prefix")} <strong className="text-[var(--text)]">{classification.suggested_class || "—"}</strong>.
        </p>
        {visibleSummaryTriggers.length > 0 ? (
          <ul className="mt-2 space-y-1 text-sm">
            {visibleSummaryTriggers.map((trigger, index) => (
              <li key={`${trigger.rule}-${index}`}>
                <span className="font-semibold">{t("classification.rule")} {trigger.rule}</span>
                {trigger.title ? ` — ${trigger.title}` : ""}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-sm text-[var(--risk-warning)]">{t("classification.no_decisive_reason")}</p>
        )}
        {hasOmittedCriteria && (
          <p className="mt-2 text-xs text-[var(--text-2)]">{t("classification.omitted_criteria")}</p>
        )}
      </div>

      <details className="rounded-sm border border-[var(--border)] p-4" data-testid="classification-rule-details">
        <summary className="cursor-pointer font-semibold text-[var(--accent-text)]">
          {t("classification.details_toggle")}
        </summary>
        <div className="mt-4 space-y-4">
          <div>
            <h3 className="font-semibold">{t("classification.rule_engine")}</h3>
            <p className="text-sm text-[var(--text-2)] mt-1">{t("classification.rule_help")}</p>
          </div>

          {rules.length > 0 ? (
            <>
              <label className="block max-w-2xl text-xs font-semibold text-[var(--text-2)]">
                {t("classification.select_rule")}
                <select
                  className="mt-1 w-full rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] px-3 py-2 text-sm text-[var(--text)]"
                  value={activeRule}
                  onChange={(event) => setSelectedRule(event.target.value)}
                  data-testid="classification-rule-select"
                >
                  {rules.map(([number, title]) => (
                    <option key={number} value={number}>{t("classification.rule")} {number} — {title}</option>
                  ))}
                </select>
              </label>

              {activeRule && (
                <div className="rounded-sm border border-[var(--border)] bg-[var(--surface-elevated)] p-4" data-testid="selected-rule-detail">
                  <div className="text-xs font-mono uppercase tracking-wider text-[var(--text-2)]">{t("classification.rule")} {activeRule}</div>
                  <p className="mt-1 text-sm font-medium">{selectedTitle}</p>
                  {selectedResults.length > 0 ? (
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-[var(--risk-success)]">{t("classification.triggered")}</span>
                      {selectedResults.map((result, index) => (
                        <span key={`${activeRule}-${index}`} className={`chip chip-${result.result}`}>{result.result}</span>
                      ))}
                    </div>
                  ) : (
                    <p className="mt-3 text-sm text-[var(--risk-warning)]">{t("classification.not_triggered")}</p>
                  )}
                </div>
              )}

              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm text-[var(--text)]">
                  <thead className="risk-table-head border-b">
                    <tr className="text-left">
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider">{t("classification.rule")}</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider">{t("classification.rule_title")}</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider">{t("classification.result")}</th>
                      <th className="px-3 py-2 font-mono text-xs uppercase tracking-wider">{t("classification.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map(([number, title], index) => {
                      const matches = triggerMap.get(number) || [];
                      return (
                        <tr key={number} className={`risk-table-row border-b ${index % 2 === 1 ? "risk-table-row-alt" : ""}`}>
                          <td className="px-3 py-3 font-mono font-bold">
                            <button type="button" className="text-[var(--accent-text)] underline underline-offset-2" onClick={() => setSelectedRule(number)}>
                              {t("classification.rule")} {number}
                            </button>
                          </td>
                          <td className="px-3 py-3">{title}</td>
                          <td className="px-3 py-3">
                            {matches.length ? matches.map((match, matchIndex) => <span key={`${number}-${matchIndex}`} className={`chip chip-${match.result} mr-1`}>{match.result}</span>) : "—"}
                          </td>
                          <td className={`px-3 py-3 text-xs ${matches.length ? "text-[var(--risk-success)]" : "text-[var(--risk-warning)]"}`}>
                            {matches.length ? t("classification.triggered") : t("classification.review")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-sm text-[var(--risk-warning)]" role="status">{t("classification.no_rules")}</div>
          )}
        </div>
      </details>

      <div className="flex flex-col gap-2">
        <p className="text-xs leading-relaxed text-[var(--text-2)]">{t("classification.review_note")}</p>
        <a
          href={classification.annex_viii_source || "https://eur-lex.europa.eu/eli/reg/2017/745/anx_8/oj"}
          target="_blank"
          rel="noreferrer"
          className="w-fit text-xs text-[var(--accent-text)] underline underline-offset-2"
        >
          {t("classification.official_source")}
        </a>
      </div>
    </section>
  );
}
